import { useCallback } from 'react'
import { useQuery, useQueryClient, UseQueryResult } from 'react-query'

import {
  filterPresetsQueryKey,
  useFilterService,
} from 'features/filters/service'
import { useFilterUIStore } from 'features/filters/store'
import { FilterUIState } from 'features/filters/store'
import type { SavedFilterPreset } from 'features/filters/types'
import { useProjectStore } from 'features/project/store'
import { ProjectState } from 'types/project'

/** `yogiql`: list the YogiQL presets, for a picker that applies conditions. */
export function useSavedFilterSelectorState(yogiql = false): {
  filterService: ReturnType<typeof useFilterService>
  projectId: string
  updateSelectedOptionField: FilterUIState['updateSelectedOptionField']
  updateOpenedOptions: FilterUIState['updateOpenedOptions']
} & UseQueryResult<SavedFilterPreset[]> {
  // bully ignore[no-service-hooks-outside-containers: sibling V1 filter-list hooks in this same directory (use-panel-filter-options.ts et al.) all call useFilterService() at the hook level — the canonical query-hook shape in this codebase. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md.]
  const filterService = useFilterService()
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const updateSelectedOptionField = useFilterUIStore(
    (state: FilterUIState) => state.updateSelectedOptionField,
  )
  const updateOpenedOptions = useFilterUIStore(
    (state: FilterUIState) => state.updateOpenedOptions,
  )

  const query = useQuery<SavedFilterPreset[]>(
    filterPresetsQueryKey(projectId, yogiql),
    () =>
      yogiql
        ? filterService.loadYogiQLFilterPresets(projectId)
        : filterService.loadFilterPresets(projectId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: !!projectId,
    },
  )

  // Both lists read one store: a refetch (after a delete) refreshes the other.
  const queryClient = useQueryClient()
  const ownRefetch = query.refetch
  const refetch = useCallback<typeof query.refetch>(
    async (options) => {
      await queryClient.refetchQueries(
        filterPresetsQueryKey(projectId, !yogiql),
        { exact: true },
      )
      return ownRefetch(options)
    },
    [queryClient, projectId, yogiql, ownRefetch],
  )

  return {
    filterService,
    projectId,
    updateSelectedOptionField,
    updateOpenedOptions,
    ...query,
    refetch,
  }
}
